import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthProvider";

import {
  SwipeableList,
  SwipeableListItem,
  TrailingActions,
  SwipeAction,
} from "react-swipeable-list";
import "react-swipeable-list/dist/styles.css";

/** =========================
 * Helpers
 * ========================= */
const norm = (s) => (s || "").toString().trim().toUpperCase();

const pillTone = (tone) => {
  if (tone === "green") return "border-emerald-500/30 bg-emerald-500/10 text-emerald-200";
  if (tone === "red") return "border-rose-500/30 bg-rose-500/10 text-rose-200";
  return "border-gray-500/30 bg-gray-500/10 text-gray-200";
};

const dotTone = (tone) => {
  if (tone === "green") return "bg-emerald-400 shadow-[0_0_0_2px_rgba(16,185,129,.15)]";
  if (tone === "red") return "bg-rose-400 shadow-[0_0_0_2px_rgba(244,63,94,.15)]";
  return "bg-gray-400 shadow-[0_0_0_2px_rgba(156,163,175,.15)]";
};

const toneOfFlow = (v) => {
  const s = norm(v);
  if (s.includes("ACC")) return "green";
  if (s.includes("DIST")) return "red";
  return "gray";
};

const botPillClass = (signal) => {
  const v = norm(signal);
  if (v === "BUY") return "border-emerald-500/30 bg-emerald-500/10 text-emerald-200";
  if (v === "WATCH") return "border-amber-500/30 bg-amber-500/10 text-amber-200";
  if (v === "AVOID") return "border-rose-500/30 bg-rose-500/10 text-rose-200";
  return "border-gray-500/30 bg-gray-500/10 text-gray-200";
};

const fmtIDR = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  return n.toLocaleString("id-ID");
};

const fmtPct = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(2)}%`;
};

const pctClass = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return "text-gray-400";
  return n > 0 ? "text-emerald-400" : "text-rose-400";
};

function FlowMini({ label, value }) {
  const tone = toneOfFlow(value);
  return (
    <div className="flex items-center gap-2 text-[12px] text-gray-400 leading-none min-w-0">
      <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${dotTone(tone)}`} />
      <span className="truncate">{label}</span>
    </div>
  );
}

/** =========================
 * Card (FINAL)
 * - Full width
 * - Price/% vertically centered
 * - Bot pill beside symbol
 * - Flow one row (Foreign + All)
 * ========================= */
function WatchlistCard({ x, onOpen }) {
  const sym = (x.symbol || "").toUpperCase();
  const bot = norm(x.bot_signal) || "—";

  return (
    <div className="w-full rounded-2xl border border-gray-800 bg-gray-900/70 px-4 py-3">
      {/* 1 wrapper flex: left grows, right fixed and vertically centered */}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onOpen();
        }}
        className="w-full text-left"
      >
        <div className="flex items-stretch justify-between gap-4">
          {/* LEFT */}
          <div className="min-w-0 flex-1">
            {/* Row 1: Symbol + Bot pill */}
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-yellow-300">★</span>

              <span className="text-[16px] font-semibold text-blue-300 tracking-wide truncate">
                {sym}
              </span>

              <span
                className={`inline-flex items-center rounded-full border px-2 py-[2px] text-[11px] font-semibold ${botPillClass(
                  bot
                )}`}
              >
                {bot}
              </span>
            </div>

            {/* Row 2: Flows (closer) */}
            <div className="mt-2 flex items-center gap-6 min-w-0">
              <FlowMini label="Foreign Flow" value={x.foreign_flow_signal} />
              <FlowMini label="All Flow" value={x.broker_all_signal} />
            </div>
          </div>

          {/* RIGHT (vertically centered) */}
          <div className="flex flex-col justify-center items-end text-right shrink-0">
            <div className="text-[18px] font-semibold text-gray-100 leading-tight">
              {fmtIDR(x.last_price)}
            </div>
            <div className={`text-[12px] leading-tight ${pctClass(x.chg_pct)}`}>
              {fmtPct(x.chg_pct)}
            </div>
          </div>
        </div>
      </button>
    </div>
  );
}

/** =========================
 * Page
 * ========================= */
export default function WatchlistPage() {
  const nav = useNavigate();
  const { token } = useAuth() || {};
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const authHeaders = useMemo(() => {
    const h = {};
    if (token) h["Authorization"] = `Bearer ${token}`;
    return h;
  }, [token]);

  const fetchWatchlist = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await fetch("/api/stocks/watchlist/snapshot?active_only=1", {
        headers: authHeaders,
      });
      const js = await r.json();
      if (!r.ok || js?.status !== "ok") throw new Error(js?.detail || js?.message || "Failed");
      setRows(js?.rows || []);
    } catch (e) {
      console.error(e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const remove = async (sym) => {
    const symbol = (sym || "").toUpperCase().trim();
    if (!symbol) return;

    // optimistic
    setRows((prev) => prev.filter((x) => (x.symbol || "").toUpperCase() !== symbol));

    try {
      const r = await fetch(`/api/stocks/watchlist/${encodeURIComponent(symbol)}?hard=0`, {
        method: "DELETE",
        headers: authHeaders,
      });
      const js = await r.json();
      if (!r.ok || js?.status !== "ok") throw new Error(js?.detail || js?.message || "Failed");
    } catch (e) {
      console.error(e);
      fetchWatchlist(); // rollback
    }
  };

  const trailingActions = (sym) => (
    <TrailingActions>
      <SwipeAction destructive onClick={() => remove(sym)}>
        <div className="h-full flex items-center justify-end pr-4 text-rose-200 font-semibold">
          Remove
        </div>
      </SwipeAction>
    </TrailingActions>
  );

  useEffect(() => {
    fetchWatchlist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="w-full p-3 pb-[calc(env(safe-area-inset-bottom)+72px)]">
      {/* header */}
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-gray-100">Watchlist</div>
        <button
          onClick={fetchWatchlist}
          className="h-8 px-3 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700 text-xs text-gray-200"
        >
          Refresh
        </button>
      </div>

      {loading && <div className="text-xs text-gray-400 py-6 text-center">Loading...</div>}

      {!loading && rows.length === 0 && (
        <div className="rounded-xl border border-gray-800 bg-gray-900 px-4 py-8 text-center text-xs text-gray-400">
          Watchlist masih kosong. Klik ★ di halaman detail saham untuk menambahkan.
        </div>
      )}

      <SwipeableList className="w-full space-y-2">
        {rows.map((x) => {
          const sym = (x.symbol || "").toUpperCase();

          return (
            <SwipeableListItem key={sym} trailingActions={trailingActions(sym)} className="w-full">
              <div className="w-full">
                <WatchlistCard x={x} onOpen={() => nav(`/stocks/${encodeURIComponent(sym)}`)} />
              </div>
            </SwipeableListItem>
          );
        })}
      </SwipeableList>
    </div>
  );
}
